import { PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { CarInformation, FitAssistantState, StepData } from '../Types/FitAssistantData.ts';
import { resetFitAssistant } from '../Thunks/Reset.ts';
import { getOptions } from '../Thunks/Queries.ts';
import { fetchListOptions } from '../Thunks/FetchListOptions.ts';
import FitAssistantStatus from '../Enums/FitAssistantStatus.ts';
import FitAssistantStep from '../Enums/FitAssistantStep.ts';
import { loadOptionsFromCookie } from '../Thunks/LoadOptionsFromCookie.ts';
import { loadOptionsFromSolutionId } from '../Thunks/LoadOptionsFromSolutionId.ts';
import { SelectedCarFromCookie } from '../Thunks/Types.ts';
import { trackView } from '../Analytics/Adobe.ts';
import FitAssistantVariation from '../Enums/FitAssistantVariation.ts';

const initialData = { ...window._THULEDATA.FitAssistant };

const initialState: FitAssistantState = {
  carInformation: [],
  lastSavedCarInformation: [],
  fitStatus: FitAssistantStatus.NoSelection,
  initialData: initialData,
  step: initialData.Steps[0].Parameter,
  stepData: [],
  confirmedCompatibility: false,
  hasError: false,
  showFitAssistant: false,
  showFitGuideModal: false,
  showFitTipsModal: false,
  showMoreInfo: false,
  showResetButtons: false,
  footerIsFixed: false,
  hasLoadedFromCookie: false,
  hasFinishedSelection: false,
  hasAddedRooftopTentsRoofRack: false,
  rooftopTentsRackAddToCartButton: null,
  rooftopTentsRackPriceContainer: null,
};

const normalizeStepForNavigation = (step: FitAssistantStep): FitAssistantStep => {
  return step === FitAssistantStep.RoofRacks ? FitAssistantStep.Variation : step;
};

const getPreviousStep = (stepData: StepData[], initialStep: FitAssistantStep): FitAssistantStep => {
  if (stepData.length === 0) {
    return initialStep;
  }
  return stepData[stepData.length - 1].parameters;
};

const resetNavigationState = (state: FitAssistantState): void => {
  state.showResetButtons = false;
  state.confirmedCompatibility = false;
  state.hasFinishedSelection = false;
};

const clearRooftopTentsRackAtcCache = (state: FitAssistantState): void => {
  state.rooftopTentsRackAddToCartButton = null;
  state.rooftopTentsRackPriceContainer = null;
};

export const FitAssistantSlice = createSlice({
  name: 'fitAssistant',
  initialState,
  reducers: {
    goBack: (state) => {
      const clearRackAtcCache =
        state.step === FitAssistantStep.RoofRacks &&
        state.initialData.FitAssistantVariation === FitAssistantVariation.RooftopTents;

      const currentStepIndex = state.stepData.findIndex((x) => x.parameters === state.step);

      let previousStep: FitAssistantStep;
      let newStepData: StepData[];
      let newCarInformation: CarInformation[];

      if (currentStepIndex !== -1) {
        newStepData = state.stepData.slice(0, currentStepIndex);
        newCarInformation = state.carInformation.slice(0, currentStepIndex);
        previousStep = getPreviousStep(newStepData, state.initialData.Steps[0].Parameter);
      } else {
        newStepData = state.stepData;
        newCarInformation = state.carInformation;
        previousStep = getPreviousStep(newStepData, state.initialData.Steps[0].Parameter);
      }

      state.stepData = newStepData;
      state.carInformation = newCarInformation;
      state.step = normalizeStepForNavigation(previousStep);
      resetNavigationState(state);
      if (clearRackAtcCache) clearRooftopTentsRackAtcCache(state);
      trackViewOnStepChange(state, previousStep);
    },
    setShowFitAssistant: (state, { payload }: PayloadAction<boolean>) => {
      state.showFitAssistant = payload;
      if (payload) trackViewOnVisibility(state, state.step);
    },
    setShowMoreInfo: (state, { payload }: PayloadAction<boolean>) => {
      state.showMoreInfo = payload;
    },
    setFitStatus: (state, { payload }: PayloadAction<FitAssistantStatus>) => {
      state.fitStatus = payload;
    },
    setShowResetButtons: (state, { payload }: PayloadAction<boolean>) => {
      state.showResetButtons = payload;
    },
    setShowFitGuideModal: (state, { payload }: PayloadAction<boolean>) => {
      state.showFitGuideModal = payload;
    },
    setShowFitTipsModal: (state, { payload }: PayloadAction<boolean>) => {
      state.showFitTipsModal = payload;
    },
    addCarInformation: (state, { payload }: PayloadAction<CarInformation[]>) => {
      state.carInformation = payload;
      if (payload?.length > 2) {
        state.lastSavedCarInformation = payload;
      }
    },
    setStepEnum: (state, { payload }: PayloadAction<FitAssistantStep>) => {
      trackViewOnStepChange(state, payload);

      state.step = payload;
    },
    setConfirmedCompatibility: (state, { payload }: PayloadAction<boolean>) => {
      state.confirmedCompatibility = payload;
    },
    setHasError: (state, { payload }: PayloadAction<boolean>) => {
      state.hasError = payload;
    },
    setHasFinishedSelection: (state, { payload }: PayloadAction<boolean>) => {
      state.hasFinishedSelection = payload;
    },
    setFooterIsFixed: (state, { payload }: PayloadAction<boolean>) => {
      state.footerIsFixed = payload;
    },
    resetContent: (state) => {
      state.step = state.initialData.Steps[0].Parameter;
      state.stepData = [];
      state.carInformation = [];
      state.lastSavedCarInformation = [];
      state.showResetButtons = false;
      state.confirmedCompatibility = false;
      state.hasFinishedSelection = false;
      state.hasAddedRooftopTentsRoofRack = false;
      clearRooftopTentsRackAtcCache(state);
    },
    addSelectedCarFromCookie: (state, { payload }: PayloadAction<SelectedCarFromCookie>) => {
      state.stepData = payload.getSliderList;
      state.carInformation = payload.getSelectedCar;
      state.lastSavedCarInformation = payload.getSelectedCar;
      if (payload.step) {
        state.step = payload.step;
      }
      state.showResetButtons = true;
    },
    addStepToList: (state, { payload }: PayloadAction<StepData>) => {
      const getDuplicatesIndex = state.stepData.findIndex((data) => data.parameters === payload.parameters);

      if (getDuplicatesIndex !== -1) {
        let updatedStepData = state.stepData.map((item, i) => (i === getDuplicatesIndex ? payload : item));
        let index = state.stepData?.findIndex((x) => x.parameters === payload.parameters);

        if (index) {
          updatedStepData = updatedStepData.slice(0, index + 1);
          state.step = payload.parameters;
        }

        state.stepData = updatedStepData;
      } else {
        state.stepData = [...state.stepData, payload];
      }
    },
    selectOption: (state, { payload }: PayloadAction<number>) => {
      if (
        state.step === FitAssistantStep.RoofRacks &&
        state.initialData.FitAssistantVariation === FitAssistantVariation.RooftopTents
      ) {
        clearRooftopTentsRackAtcCache(state);
      }
      let data = state.stepData
        .find((x) => x.parameters === state.step)
        ?.data.map((option, index) =>
          index === payload ? { ...option, Selected: true } : { ...option, Selected: false }
        );
      let updatedStepData = state.stepData.map((stepData) =>
        stepData.parameters === state.step ? { ...stepData, data: data } : stepData
      );

      state.stepData = updatedStepData as StepData[];
    },
    unselectOptions: (state) => {
      if (
        state.step === FitAssistantStep.RoofRacks &&
        state.initialData.FitAssistantVariation === FitAssistantVariation.RooftopTents
      ) {
        clearRooftopTentsRackAtcCache(state);
      }
      let data = state.stepData
        .find((x) => x.parameters === state.step)
        ?.data.map((option) => {
          return { ...option, Selected: false };
        });
      let updatedStepData = state.stepData.map((stepData) =>
        stepData.parameters === state.step ? { ...stepData, data: data } : stepData
      );

      state.stepData = updatedStepData as StepData[];
    },
    setHasAddedRooftopTentsRoofRack: (state, { payload }: PayloadAction<boolean>) => {
      state.hasAddedRooftopTentsRoofRack = payload;
    },
    setRooftopTentsRackAtcCache: (
      state,
      {
        payload,
      }: PayloadAction<{
        addToCartButton: FitAssistantState['rooftopTentsRackAddToCartButton'];
        priceContainer: FitAssistantState['rooftopTentsRackPriceContainer'];
      }>
    ) => {
      state.rooftopTentsRackAddToCartButton = payload.addToCartButton;
      state.rooftopTentsRackPriceContainer = payload.priceContainer;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetFitAssistant.pending, (state) => {
      state.fitStatus = FitAssistantStatus.NoSelection;
      FitAssistantSlice.caseReducers.resetContent(state);
    });
    builder.addCase(resetFitAssistant.fulfilled, (state) => {
      state.fitStatus = FitAssistantStatus.NoSelection;
    });
    builder.addMatcher(
      isAnyOf(loadOptionsFromCookie.fulfilled, loadOptionsFromSolutionId.fulfilled, loadOptionsFromSolutionId.rejected),
      (state) => {
        state.hasLoadedFromCookie = true;
      }
    );
    builder.addMatcher(
      isAnyOf(getOptions.fulfilled, fetchListOptions.fulfilled),
      (state, { payload }: PayloadAction<StepData>) => {
        let stepToListpayload = { payload } as PayloadAction<StepData>;
        let stepEnumPayload = { payload: stepToListpayload.payload.parameters } as PayloadAction<FitAssistantStep>;
        FitAssistantSlice.caseReducers.addStepToList(state, stepToListpayload);
        if (stepEnumPayload.payload != FitAssistantStep.RoofRacks) {
          FitAssistantSlice.caseReducers.setStepEnum(state, stepEnumPayload);
        }
      }
    );
  },
});

const trackViewOnStepChange = (state: FitAssistantState, step: FitAssistantStep) => {
  // Avoid duplicate year event when first navigating and then loading options
  if (state.step === FitAssistantStep.Mounting) return;

  if (
    step === FitAssistantStep.Make ||
    step === FitAssistantStep.Model ||
    step === FitAssistantStep.Year ||
    step === FitAssistantStep.VanSize ||
    (step === FitAssistantStep.Variation && state.step !== FitAssistantStep.Variation)
  ) {
    trackView(
      state.initialData.FitAssistantVariation,
      step,
      state.initialData.IsYearFirst,
      state.carInformation,
      state.hasLoadedFromCookie
    );
  }
};

const trackViewOnVisibility = (state: FitAssistantState, step: FitAssistantStep) => {
  let shortVersions = [
    FitAssistantStep.Make,
    FitAssistantStep.Model,
    FitAssistantStep.Year,
    FitAssistantStep.Variation,
    FitAssistantStep.VanSize,
  ];

  if (state.stepData.length > 0 && shortVersions.includes(step))
    trackView(
      state.initialData.FitAssistantVariation,
      step,
      state.initialData.IsYearFirst,
      state.carInformation,
      state.hasLoadedFromCookie
    );
};

export const {
  addCarInformation,
  addSelectedCarFromCookie,
  addStepToList,
  goBack,
  resetContent,
  selectOption,
  setConfirmedCompatibility,
  setFitStatus,
  setFooterIsFixed,
  setHasError,
  setHasFinishedSelection,
  setShowFitAssistant,
  setShowFitGuideModal,
  setShowFitTipsModal,
  setShowMoreInfo,
  setShowResetButtons,
  setStepEnum,
  unselectOptions,
  setHasAddedRooftopTentsRoofRack,
  setRooftopTentsRackAtcCache,
} = FitAssistantSlice.actions;

export default FitAssistantSlice.reducer;
